import { inject, Injectable } from '@angular/core';
import { System2d } from '../asset/asset.dto';
import { logger, createBatch, deleteBatch, SYSTEMS_COLLECTION, updateBatch, WW_WAREHOUSES_COLLECTION } from '@damofire/data-commons';
import { Firestore } from '@angular/fire/firestore';
import { collection, doc, getDoc, getDocs, orderBy, query, where } from 'firebase/firestore';
import { generateAndAssignSystemIds, SystemDrawingService } from './system.service';
import { AuthDtoSignerService } from '@damofire/data-security';

@Injectable()
export class FirebaseSystemDrawingService extends SystemDrawingService {
  private readonly firestore = inject(Firestore);
  private readonly signerService: AuthDtoSignerService = inject(AuthDtoSignerService);

  async get(division: string, system: string): Promise<System2d> {
    const docRef = doc(this.firestore, `${WW_WAREHOUSES_COLLECTION}/${division}/${SYSTEMS_COLLECTION}/${system}`);
    const snapshot = await getDoc(docRef);
    if (snapshot.exists()) {
      return { ...snapshot.data(), _id: snapshot.id } as System2d;
    }
    return null;
  }

  async reloadSystems(): Promise<void> {
    const divisionId = this.divisionId();
    const section = this.section();

    if (!divisionId) {
      return;
    }

    logger.debug('Searching for warehouse systems', { divisionId, category: 'system' });

    let queryFn;
    if (section) {
      queryFn = query(collection(this.firestore, `${WW_WAREHOUSES_COLLECTION}/${divisionId}/${SYSTEMS_COLLECTION}`), where('section', '==', section), orderBy('number'));
    } else {
      queryFn = query(collection(this.firestore, `${WW_WAREHOUSES_COLLECTION}/${divisionId}/${SYSTEMS_COLLECTION}`), orderBy('number'));
    }

    const snapshot = await getDocs(queryFn);
    this.systems.set(snapshot.docs.map((d) => ({ ...(d.data() as System2d), _id: d.id }) as System2d));
  }

  async create(divisionId: string, systems: System2d[]): Promise<void> {
    const systemsWithIds: System2d[] = generateAndAssignSystemIds(systems);

    for (const segment of systemsWithIds) {
      await this.signerService.sign(segment, true);
    }

    return createBatch(`${WW_WAREHOUSES_COLLECTION}/${divisionId}/${SYSTEMS_COLLECTION}`, systemsWithIds);
  }

  async delete(divisionId: string, segments: System2d[]): Promise<void> {
    return deleteBatch(`${WW_WAREHOUSES_COLLECTION}/${divisionId}/${SYSTEMS_COLLECTION}`, segments);
  }

  async update(divisionId: string, segments: System2d[]): Promise<void> {
    for (const segment of segments) {
      await this.signerService.sign(segment, false);
    }

    return updateBatch(`${WW_WAREHOUSES_COLLECTION}/${divisionId}/${SYSTEMS_COLLECTION}`, segments);
  }
}
